import * as React from 'react';
import { CCSF_HERO_INTERVAL_MS, CCSF_HERO_SIZES, CCSF_HERO_SLIDES, CcsfHeroSlide } from './ccsfHeroAssets';

interface State {
  index: number;
  paused: boolean;
  reducedMotion: boolean;
}

export default class CcsfHeroSlideshow extends React.Component<{}, State> {
  public state: State = {
    index: 0,
    paused: false,
    reducedMotion: false
  };

  private timer: number | null = null;
  private rootRef: HTMLDivElement | null = null;

  public componentDidMount() {
    const motion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    const reducedMotion = Boolean(motion && motion.matches);
    this.setState({ reducedMotion, paused: reducedMotion }, () => {
      if (!reducedMotion) {
        this.startTimer();
      }
    });
    if (motion) {
      if (motion.addEventListener) {
        motion.addEventListener('change', this.onMotionChange);
      } else if ((motion as MediaQueryList).addListener) {
        (motion as MediaQueryList).addListener(this.onMotionChange);
      }
    }
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  public componentWillUnmount() {
    this.clearTimer();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  private onMotionChange = (event: MediaQueryListEvent) => {
    this.setState({ reducedMotion: event.matches, paused: event.matches }, () => {
      if (event.matches) {
        this.clearTimer();
        this.setState({ index: 0 });
      } else {
        this.startTimer();
      }
    });
  }

  private onVisibility = () => {
    if (document.hidden) {
      this.clearTimer();
      return;
    }
    if (!this.state.reducedMotion && !this.state.paused) {
      this.startTimer();
    }
  }

  private startTimer = () => {
    this.clearTimer();
    if (this.state.reducedMotion || this.state.paused || document.hidden) {
      return;
    }
    this.timer = window.setInterval(this.next, CCSF_HERO_INTERVAL_MS);
  }

  private clearTimer = () => {
    if (this.timer != null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  private next = () => {
    this.setState((state) => ({
      index: (state.index + 1) % CCSF_HERO_SLIDES.length
    }));
  }

  private prev = () => {
    this.setState((state) => ({
      index: (state.index + CCSF_HERO_SLIDES.length - 1) % CCSF_HERO_SLIDES.length
    }));
  }

  private goTo = (index: number) => {
    this.setState({ index, paused: true }, this.clearTimer);
  }

  private pause = () => {
    this.setState({ paused: true }, this.clearTimer);
  }

  private resume = () => {
    if (this.state.reducedMotion) {
      return;
    }
    this.setState({ paused: false }, this.startTimer);
  }

  private togglePause = () => {
    if (this.state.paused || this.state.reducedMotion) {
      this.resume();
    } else {
      this.pause();
    }
  }

  private onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.pause();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.pause();
      this.prev();
    }
  }

  private renderSlide(slide: CcsfHeroSlide, index: number) {
    const active = index === this.state.index;
    const eager = index === 0;
    if (slide.photo) {
      return (
        <div
          key={slide.id}
          className={'ccsf-prelaunch__hero-slide' + (active ? ' is-active' : '')}
          data-slide-id={slide.id}
          aria-hidden={!active}
        >
          <picture>
            <source type="image/webp" srcSet={slide.photo.webpSrcSet} sizes={CCSF_HERO_SIZES} />
            <img
              alt={slide.story || 'San Francisco Environment Challenge'}
              title={slide.story || 'San Francisco Environment Challenge'}
              className="ccsf-prelaunch__hero-img"
              src={slide.photo.jpegSrc}
              srcSet={slide.photo.jpegSrcSet}
              sizes={CCSF_HERO_SIZES}
              decoding="async"
              loading={eager ? 'eager' : 'lazy'}
              style={{ objectPosition: slide.photo.objectPosition }}
            />
          </picture>
        </div>
      );
    }
    return (
      <div
        key={slide.id}
        className={'ccsf-prelaunch__hero-slide ccsf-prelaunch__hero-slide--pending' + (active ? ' is-active' : '')}
        aria-hidden={true}
        data-dev-note={slide.pendingNote}
      >
        <div className="ccsf-prelaunch__hero-pending">
          <span>{slide.pendingNote}</span>
        </div>
      </div>
    );
  }

  public render() {
    const { index, paused, reducedMotion } = this.state;
    const isPaused = paused || reducedMotion;
    return (
      <div
        className="ccsf-prelaunch__hero-media"
        ref={(el) => { this.rootRef = el; }}
        onMouseEnter={this.pause}
        onMouseLeave={this.resume}
        onFocus={this.pause}
        onBlur={(event) => {
          if (this.rootRef && !this.rootRef.contains(event.relatedTarget as Node)) {
            this.resume();
          }
        }}
        onKeyDown={this.onKeyDown}
      >
        <div className="ccsf-prelaunch__hero-slides" aria-hidden={true}>
          {CCSF_HERO_SLIDES.map((slide, i) => this.renderSlide(slide, i))}
        </div>
        <div className="ccsf-prelaunch__hero-overlay" aria-hidden={true} />
        <div className="ccsf-prelaunch__hero-controls" role="group" aria-label="Hero slideshow controls">
          <button
            type="button"
            className="ccsf-prelaunch__hero-nav"
            aria-label="Previous hero image"
            onClick={() => { this.pause(); this.prev(); }}
          >
            ‹
          </button>
          <div className="ccsf-prelaunch__hero-dots">
            {CCSF_HERO_SLIDES.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                aria-label={`Show hero image ${i + 1} of ${CCSF_HERO_SLIDES.length}`}
                aria-current={i === index ? 'true' : undefined}
                className={'ccsf-prelaunch__hero-dot' + (i === index ? ' is-active' : '')}
                onClick={() => this.goTo(i)}
              />
            ))}
          </div>
          <button
            type="button"
            className="ccsf-prelaunch__hero-nav"
            aria-label="Next hero image"
            onClick={() => { this.pause(); this.next(); }}
          >
            ›
          </button>
          {!reducedMotion && (
            <button
              type="button"
              className="ccsf-prelaunch__hero-pause"
              aria-pressed={isPaused}
              aria-label={isPaused ? 'Play hero slideshow' : 'Pause hero slideshow'}
              onClick={this.togglePause}
            >
              {isPaused ? 'Play' : 'Pause'}
            </button>
          )}
        </div>
        <div className="ccsf-prelaunch__sr-only" aria-live="polite">
          {reducedMotion
            ? 'Automatic slideshow is off because reduced motion is preferred.'
            : (isPaused
              ? 'Hero slideshow paused.'
              : 'Hero images rotate automatically. Use previous, next, or pause controls to change the background.')}
        </div>
      </div>
    );
  }
}
